import type { FormContext } from 'vee-validate'
import type { UseRegistrationData } from '~/store-v2/registration/useRegistration'
import { cpf } from '@solfacil/frontend-utils'
import * as z from 'zod'

export function getSchema(t: any) {
  const base = z.object({
    is_under_customer_name: z.boolean().default(true),
    electricity_bill_document: z.string({ message: t('form.required') }),
    additional_document: z.string().optional().default(''),
    comment: z.string().optional().default(''),
    distributor_id: z.number({ message: t('form.required') }).min(1, t('form.required')),
    grid_access_approval_required: z.boolean().optional().default(false),
    grid_access_approval_document: z.string().optional().default(''),
  })
  return z.discriminatedUnion('is_under_customer_name', [
    base.extend({
      is_under_customer_name: z.literal(true),
      holder_cpf: z.string().optional(),
      employment_proof_document: z.string().optional(),
    }),
    base.extend({
      is_under_customer_name: z.literal(false),
      holder_cpf: z.string({ message: t('form.required') }).refine(v => cpf.validInput(v), { message: t('form.invalid_cpf') }),
      employment_proof_document: z.string({ message: t('form.required') }),
    }),
  ]).superRefine((data, ctx) => {
    if (data.grid_access_approval_required && !data.grid_access_approval_document) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['grid_access_approval_document'],
        message: t('formalization.electricity_bill.access_opinion.required_error'),
      })
    }
  })
}

export type FormSchema = z.infer<ReturnType<typeof getSchema>>
export type Form = FormContext<Partial<FormSchema>>
export type Field = keyof(FormSchema)

export type DocumentFields = 'electricity_bill_document' | 'additional_document' | 'employment_proof_document' | 'grid_access_approval_document'

export function getInitialValues(): Partial<FormSchema> {
  return {
    is_under_customer_name: true,
    grid_access_approval_required: false,
    grid_access_approval_document: '',
  }
}

export function extractDocumentEntries(form: Form, options: { includeAccessOpinion?: boolean } = {}) {
  const includeAccessOpinion = options.includeAccessOpinion ?? true

  return Object.entries(form.values)
    .filter(([key]) => includeAccessOpinion || key !== 'grid_access_approval_document')
    .map(([key, value]) => /document/i.test(key) && value ? ({ type: key.replace('_document', ''), docstorage_id: value }) : null)
    .filter(doc => doc !== null)
}

type AnalysisError = NonNullable<UseRegistrationData>['errors'][number]

export function extractErrorsByField(errors: AnalysisError[] | undefined): Record<Field, AnalysisError> {
  const mappings: Record<string, Field> = {
    'project.electricity_bill.holder_cpf': 'holder_cpf',
    'project.electricity_bill.distributor_id': 'distributor_id',
    'project.documents.electricity_bill': 'electricity_bill_document',
    'project.documents.additional': 'additional_document',
    'project.documents.employment_proof': 'employment_proof_document',
    'project.documents.grid_access_approval': 'grid_access_approval_document',
    'project.comment': 'comment',
    'project.document.electricity_bill': 'electricity_bill_document',
    'project.document.additional': 'additional_document',
    'project.document.employment_proof': 'employment_proof_document',
    'project.document.grid_access_approval': 'grid_access_approval_document',
  } as const

  return (errors || [])
    .filter(error => !!error)
    .reduce((acc, error) => {
      const field = mappings[error.field]
      if (field) {
        acc[field] = error
      }
      return acc
    }, {} as Record<Field, AnalysisError>)
}
